import { z } from 'zod'
import { oc } from '@orpc/contract'
import {
  AGENDA_APPOINTMENT_STATUSES,
  AGENDA_READINESS,
} from '../../appointment/handlers/list-appointment-agenda.contract'
import { kateCheckInStatusSchema } from '../../kate/handlers/kate-checkin.contract'

export const BRIEFING_ITEM_KINDS = [
  'kate-question',
  'kate-callback',
  'message',
  'task',
  'result',
  'guidance',
  'note',
] as const
export const BRIEFING_GROUPS = ['before-visits', 'needs-decision', 'labs-docs'] as const
export const BRIEFING_DISMISS_REASONS = ['addressed', 'not-relevant', 'duplicate'] as const
export const BRIEFING_SOURCES = [
  'kate',
  'messages',
  'tasks',
  'results',
  'guidance',
  'notes',
] as const
export const BRIEFING_SEVERITIES = ['critical', 'high'] as const
// The "due soon" window for tasks outside today's visits (R15): due by the end of tomorrow.
export const BRIEFING_TASK_DUE_SOON_DAYS = 1

export const briefingItemStateSchema = z
  .discriminatedUnion('hidden', [
    z.object({
      hidden: z.literal('dismissed'),
      reason: z.enum(BRIEFING_DISMISS_REASONS),
      at: z.string(),
    }),
    z.object({
      hidden: z.literal('reminded'),
      remindUntil: z.string(),
      at: z.string(),
    }),
  ])
  .nullable()

const briefingPatientSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  // Presigned, expiring URL of the first Patient.photo.
  photoUrl: z.string().nullable(),
})

export const briefingItemSchema = z.object({
  // Stable across reloads: `<kind>:<sourceId>` (guidance: `guidance:<patientId>:<topic>`).
  id: z.string(),
  // Changes only when the item materially changes, so a dismissal of an older version no
  // longer hides it (R21).
  version: z.string(),
  kind: z.enum(BRIEFING_ITEM_KINDS),
  group: z.enum(BRIEFING_GROUPS),
  // The id of the record the item reads (QuestionnaireResponse, thread, board item,
  // DiagnosticReport, topic slug, DocumentReference).
  sourceId: z.string(),
  patient: briefingPatientSchema,
  appointmentId: z.string().nullable(),
  title: z.string(),
  detail: z.string().nullable(),
  // Visit Guidance only: the question the report suggests asking the patient.
  question: z.string().optional(),
  whyNow: z.string(),
  source: z.object({ label: z.string(), date: z.string().nullable() }),
  // Free text the patient said to Kate, shown as unverified.
  patientReported: z.boolean(),
  severity: z.enum(BRIEFING_SEVERITIES).nullable(),
  // Where the record shows in the patient's chart: Clinical Summary, Risks & Gaps (at the
  // topic), the patient's messages (at the thread) or the progress note.
  href: z.string(),
  // Care tasks only: the status cell "Mark done" sets to Done, and the group the task sits in
  // (Done moves it to Completed); Undo puts both back.
  taskStatus: z
    .object({ columnId: z.string(), value: z.string().nullable(), groupId: z.string() })
    .optional(),
  state: briefingItemStateSchema,
})

export const briefingAppointmentSchema = z.object({
  appointmentId: z.string(),
  encounterId: z.string().nullable(),
  start: z.string().nullable(),
  end: z.string().nullable(),
  patient: briefingPatientSchema.nullable(),
  visitReason: z.string().nullable(),
  readiness: z.enum(AGENDA_READINESS),
  // The booking's own status (pending = not confirmed yet), for the visit card after its time.
  appointmentStatus: z.enum(AGENDA_APPOINTMENT_STATUSES),
  // Null when the returning-patient lookup failed.
  firstVisit: z.boolean().nullable(),
  // The Kate check-in is neither completed nor reviewed; false when Kate could not be read.
  notCheckedIn: z.boolean(),
  kate: z
    .object({
      status: kateCheckInStatusSchema,
      completedAt: z.string().nullable(),
      // When staff last sent or copied the check-in link, and how it went out.
      sentAt: z.string().nullable(),
      sentVia: z.enum(['sms', 'email', 'link']).nullable(),
      language: z.string().nullable(),
      // What Kate confirmed (the check-in rows that need nothing from anyone).
      facts: z.array(z.string()),
      needsReview: z.boolean(),
    })
    .nullable(),
})

/**
 * The signed-in provider's briefing for one local day (PD-1936): today's appointments and
 * every unresolved item about them that needs the provider, each with the caller's own
 * dismiss / remind state. Reading it changes nothing in the sources.
 */
export const briefingDay = oc
  .route({ method: 'GET', path: '/v2/briefing/day' })
  .input(
    z.object({
      date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'date must be YYYY-MM-DD'),
      // The browser's IANA time zone; the day runs from local midnight to midnight there.
      timeZone: z
        .string()
        .min(1)
        .refine(
          (tz) => {
            try {
              new Intl.DateTimeFormat('en-US', { timeZone: tz })
              return true
            } catch {
              return false
            }
          },
          { message: 'Unknown time zone' },
        ),
    }),
  )
  .output(
    z.object({
      generatedAt: z.string(),
      practitioner: z.object({ id: z.string(), name: z.string().nullable() }),
      counts: z.object({
        visits: z.number().int(),
        firstTime: z.number().int(),
        returning: z.number().int(),
        // Visible items.
        attention: z.number().int(),
        notCheckedIn: z.number().int(),
        // Items dismissed or waiting on a reminder.
        hidden: z.number().int(),
      }),
      appointments: z.array(briefingAppointmentSchema),
      items: z.array(briefingItemSchema),
      // A source that failed reads `unavailable`, never as "nothing found" (R16).
      sources: z.array(
        z.object({
          kind: z.enum(BRIEFING_SOURCES),
          status: z.enum(['ok', 'unavailable']),
          asOf: z.string(),
        }),
      ),
    }),
  )
